/**
 * @file: types.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { AppNavParams } from '@euler/app/Routes';
import { ImageAnnotationMetadata } from '@euler/app/flows/media/annotation/functions/AnnotationMetadata';
import { MediaFileUploadController } from '@euler/app/flows/task/functions/task-detail/MediaFileUploadController';
import { InventoryQuery } from '@euler/functions/useInventory';
import {
  FormController,
  ValidateErrorObject,
} from '@euler/lib/form/controller';
import {
  InspectionTemplate,
  OrderDetail,
  ServiceSubject,
  StoreConfiguration,
  VehicleInfoCore,
} from '@euler/model';
import {
  VehicleInfo,
  VehicleInspectionTask,
  VehicleReceptionOrder,
} from '@euler/model/entity';
import {
  PreInspection,
  SiteInspectionStaged,
  TaskBasicInfo,
} from '@euler/model/task-detail';
import { VehicleServiceInfo } from '@euler/services';
import { StackNavigationProp } from '@react-navigation/stack';
import { Draft } from 'immer';
import { MutableRefObject, ReactNode } from 'react';
import { BehaviorSubject, Subject } from 'rxjs';
import type { OrderNavParams } from './OrderFlow';

import { FacadeMediaInfo } from '@euler/app/flows/task/pages/pre-inspection/functions/types';
import {
  CarwashType,
  CustomerType,
  InspectionOrderType,
  OldPartDisposalType,
  OrderReservationType,
  OrderReturnType,
} from '@euler/model/enum';
import { Maybe } from '@euler/types';
import { StackHeaderMode } from '@react-navigation/stack/lib/typescript/src/types';

export type OrderFormMode = 'new' | 'update';
export type OrderFormType = 'main' | 'child';

export type OrderOcrImageInfo = {
  imageUrl: string;
  timestamp: Date;
};

export type OcrImageType = 'vin' | 'plate';

export type OrderExtra = OrderDetail & {
  preInspection?: SiteInspectionStaged[];
  signImageUrl?: string;
  signedAt?: string | Date;
};

export type OrderNavigateAwayModalAction =
  | {
      type: 'show';
    }
  | {
      type: 'cancel';
    }
  | {
      type: 'return-save-draft';
    }
  | {
      type: 'return-discard-draft';
    };

export type OrderFileUploadRequestContext =
  | {
      type: 'dashboard-image';
    }
  | {
      type: 'facade-image';
      siteId: number;
      mediaKey?: string;
      annotationMetadata?: ImageAnnotationMetadata;
    }
  | {
      type: 'digital-sign';
    }
  | {
      type: 'extended-property';
      propertyId: string;
      mediaKey?: string;
    };

export type OrderFlowStep = 'order-info' | 'preview';

export type OrderForm = {
  licensePlateNo: string;
  vin: string;
  /**
   * Set when the vehicle has no ISO VIN: `vin` carries the placeholder and
   * this is the number the shop reads off the car. Hong Kong and imported
   * vehicles still use one.
   */
  chassisNo?: string;
  mileage: number;
  serviceType?: InspectionOrderType;
  serviceAgentInfo?: {
    serviceAgentId: number;
    name: string;
  };
  dashboardImgUrl?: string;
  vehicleInfo?: Omit<
    VehicleInfoCore,
    'licensePlateNo' | 'vin' | 'vehicleMileage'
  >;
  remark?: string;
  estimatedFinishTime?: Date | string;
  estimatedFinishTimeUpdateReason?: string;
  estimatedFinishTimeUpdateRemark?: string;
  tags?: string[];
  customerName?: string;
  customerMobile?: string;
  customerIsOnSite?: boolean;
  customerType?: CustomerType;
  carwash?: boolean;
  carwashType?: CarwashType;
  returnType?: OrderReturnType;
  oldPartDisposalType?: OldPartDisposalType;
  reservationType?: OrderReservationType;
  // The reservation this order is placed for (from-reservation entry, or picked
  // by plate match). On create, the backend converts it + its parent lead.
  reservationId?: string;
  insuranceExpiryDate?: string | Date;
  isSpecialLicensePlateNo: boolean;
  isNonStandardVin: boolean;

  // dummy field, used for validation purpose only
  serviceSubjects?: any;
  extendedProperties?: any;
  facadeInspectionResults?: any;
};

export type OrderPageCapabilities = {
  readonlyInspectionSubjects: Set<string>;
  readonlyConstructionSubjects: Set<string>;
  preInspectionReadonly?: boolean;
};

export type OrderFlowContextProps = {
  theme: {
    linkColor: string;
    labelColor: string;
    placeholderColor: string;
    iconColor: string;
  };
  mode: OrderFormMode;
  type: OrderFormType;
  mainOrderNo?: string;
  capabilities?: OrderPageCapabilities;
  inspectionTemplates: InspectionTemplate[];
  /** The facade template a pre-inspection recorded from this order applies. */
  facadeInspectionTemplate?: InspectionTemplate;
  step$: BehaviorSubject<OrderFlowStep>;
  draftId: string;
  dirty$: BehaviorSubject<boolean>;
  headerMode$: BehaviorSubject<StackHeaderMode>;
  skipJobAssignment$: BehaviorSubject<boolean>;
  navigateAwayAction$: Subject<OrderNavigateAwayModalAction>;
  showDraftModal$: BehaviorSubject<boolean>;
  navigation: StackNavigationProp<AppNavParams & OrderNavParams>;
  storeConfiguration: StoreConfiguration;
  skipDirtyCheck: MutableRefObject<boolean>;
  isFinished: MutableRefObject<boolean>;
  basicInfo?: TaskBasicInfo;
  formController: FormController<OrderForm>;
  lastVehicleInfo$: BehaviorSubject<VehicleInfo | undefined>;
  vehicleImageUrl$: BehaviorSubject<string | undefined>;
  lastServiceInfo$: BehaviorSubject<VehicleServiceInfo | undefined>;
  orderDetail$: BehaviorSubject<OrderExtra>;
  inventoryQuery: InventoryQuery;
  fileUploader: MediaFileUploadController<OrderFileUploadRequestContext>;
  serviceSubjectQuery$: BehaviorSubject<string>;
  showCustomServiceSubjectModal$: BehaviorSubject<boolean | ServiceSubject>;
  draftEnabled: MutableRefObject<boolean>;
  showDigitalSignModal$: BehaviorSubject<boolean>;
  showTermsOfServiceModal$: BehaviorSubject<boolean>;
  isSignUnavailable$: BehaviorSubject<boolean>;
  loadDraft: (draft: OrderDraft) => void;
  saveDraft: () => void;
  discardDraft: () => void;
  updateDetail: (recipe: (draft: Draft<OrderExtra>) => void) => void;
  finish: (options?: {
    simulate?: boolean;
    forceDiscardDraft?: boolean;
    callback?: () => void;
  }) => void;
  onNavigateToExistingTask?: (taskNo: string) => void;
  onCreated:
    | ((order: VehicleReceptionOrder, task: VehicleInspectionTask) => void)
    | undefined;
  onUpdated: ((order: VehicleReceptionOrder) => void) | undefined;
  ocrImageInfo: {
    licensePlateNo$: BehaviorSubject<OrderOcrImageInfo | undefined>;
    vin$: BehaviorSubject<OrderOcrImageInfo | undefined>;
  };
};

export type OrderDraft = {
  id: string;
  timestamp: number;
  form: OrderForm;
  lastVehicleInfo?: VehicleInfo;
  vehicleImageUrl?: string;
  orderDetail?: OrderExtra;
  ocrImageInfo?: {
    licensePlateNo?: OrderOcrImageInfo;
    vin?: OrderOcrImageInfo;
  };
};

export type FacadeScreenEvent =
  | {
      type: 'retryUpload';
      media: FacadeMediaInfo;
    }
  | {
      type: 'add';
      code: string;
    };

export type OrderQrcodeUrlParams = {
  lpn: string;
  logo?: boolean;
  size?: number;
  __fake__?: boolean;
};

export type OrderDetailContextProps = {
  orderNo: string;
  licensePlateNo: string;
  vin: string;
  tags: Maybe<string[]>;
  vehicleMileage: number;
  vehicleName: string;
  vehicleBrandName: string;
  serviceAgentName: string;
  carwashType: Maybe<CarwashType>;
  reservationType: Maybe<OrderReservationType>;
  oldPartDisposalType: Maybe<OldPartDisposalType>;
  returnType: Maybe<OrderReturnType>;
  estimatedFinishTime: Maybe<string | Date>;
  insuranceExpiryDate?: Maybe<string | Date>;
  customerType: Maybe<CustomerType>;
  customerName: Maybe<string>;
  customerMobile: Maybe<string>;
  dashboardImageUrl: Maybe<string>;
  signImgUrl: Maybe<string>;
  remark?: Maybe<string>;
  preInspection: Maybe<PreInspection>;
  orderDetail: Maybe<OrderDetail>;
  getQrcodeImageUrl: Maybe<(params: OrderQrcodeUrlParams) => Promise<string>>;
};

export type OrderFormRenderingContext = {
  mode: OrderFormMode;
  type: OrderFormType;
  isEntryOrder: boolean;
  hasChildOrders: boolean;
  storeConfiguration: StoreConfiguration;
};

export type OrderFormSection = {
  name: string;
  fields: OrderFormField[];
  renderTitleRight?: () => ReactNode;
};

export type OrderFormField = {
  render: () => ReactNode;
};

export class ExtendedPropertyValidationErrorInfo extends ValidateErrorObject {
  readonly errors: { [p: string]: string } = {};
  constructor() {
    super(new Error('Extended properties validation error'));
  }
}
